import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import Cookies from "js-cookie";

const RTL_LANGS = ["ar", "ku"];
const SUPPORTED_LANGS = ["ar", "en", "ku", "tr"];

export const APP_LANGUAGES = [
  { value: "ku", label: "کوردی" },
  { value: "ar", label: "العربية" },
  { value: "en", label: "English" },
  { value: "tr", label: "Türkçe" },
];

export const normalizeLang = (language) => {
  const code = String(language || "en").split("-")[0].toLowerCase();
  return SUPPORTED_LANGS.includes(code) ? code : "en";
};

export const isRtlLang = (language) => RTL_LANGS.includes(normalizeLang(language));

/**
 * AntD Table's `fixed` prop only accepts literal 'left'/'right' (no logical
 * 'start'/'end'), so a column meant to always be the first one you see must
 * pick a physical side based on the resolved reading direction.
 */
export const fixedStart = (language) => (isRtlLang(language) ? "right" : "left");

let dirAnimTimer = null;
const pulseUiMotion = (className = "ui-dir-animating", ms = 420) => {
  const root = document.documentElement;
  root.classList.add(className);
  if (dirAnimTimer) clearTimeout(dirAnimTimer);
  dirAnimTimer = setTimeout(() => {
    root.classList.remove(className);
    dirAnimTimer = null;
  }, ms);
};

const applyDirection = (language) => {
  const lang = normalizeLang(language);
  const dir = RTL_LANGS.includes(lang) ? "rtl" : "ltr";
  const root = document.documentElement;
  const prevDir = root.getAttribute("dir");
  root.setAttribute("dir", dir);
  root.setAttribute("lang", lang);

  if (prevDir && prevDir !== dir) {
    pulseUiMotion("ui-dir-animating", 420);
  }
};

const persistLang = (lang) => {
  try {
    localStorage.setItem("selectedLanguage", lang);
    localStorage.setItem("i18nextLng", lang);
    Cookies.set("language", lang);
  } catch { /* ignore storage errors */ }
};

/** @deprecated no runtime translation overlay */
export const setAfterLanguageChange = () => {};

let langSwitchSeq = 0;

const changeLanguage = async (language) => {
  const lang = normalizeLang(language);
  const seq = ++langSwitchSeq;
  persistLang(lang);
  applyDirection(lang);

  try {
    if (!i18n.hasResourceBundle(lang, "translation")) {
      await i18n.loadLanguages(lang);
    }
    if (seq !== langSwitchSeq) return lang;
    await i18n.changeLanguage(lang);
    if (seq !== langSwitchSeq) return lang;
    if (!document.documentElement.classList.contains("ui-dir-animating")) {
      pulseUiMotion("ui-dir-animating", 320);
    }
  } catch {
    if (seq === langSwitchSeq) applyDirection(lang);
  }

  return lang;
};

// Bump when locale files change on disk from the editor (helps bypass stale cache).
const I18N_VERSION = process.env.REACT_APP_I18N_VERSION || "20260715e";

const savedLang = normalizeLang(
  Cookies.get("language") || localStorage.getItem("selectedLanguage") || localStorage.getItem("i18nextLng") || "en"
);

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: SUPPORTED_LANGS,
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    cleanCode: true,
    fallbackLng: { ku: ["en"], tr: ["en"], ar: ["en"], default: ["en"] },
    lng: savedLang,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
      queryStringParams: { v: I18N_VERSION },
    },
    detection: {
      order: ["localStorage", "cookie", "navigator"],
      caches: ["localStorage", "cookie"],
      lookupLocalStorage: "selectedLanguage",
      lookupCookie: "language",
    },
  });

i18n.on("languageChanged", (lng) => {
  applyDirection(lng);
  persistLang(normalizeLang(lng));
});

applyDirection(savedLang);

// eslint-disable-next-line import/no-anonymous-default-export
export default { changeLanguage, i18n, normalizeLang };
